const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'blockquote',
  'pre',
  'code',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'span',
  'div',
]);

const BLOCKED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'meta',
  'link',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
]);

const SAFE_IMG_DATA_URL = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

const isSafeHref = (url: string): boolean => {
  const value = url.trim().toLowerCase();
  return (
    value.startsWith('https://') ||
    value.startsWith('http://') ||
    value.startsWith('mailto:') ||
    value.startsWith('tel:') ||
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../') ||
    value.startsWith('#')
  );
};

const isSafeSrc = (url: string): boolean => {
  const value = url.trim().toLowerCase();
  return (
    value.startsWith('https://') ||
    value.startsWith('http://') ||
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../') ||
    SAFE_IMG_DATA_URL.test(value)
  );
};

export const sanitizeRichHtml = (html: string): string => {
  if (!html) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  for (const blocked of Array.from(doc.querySelectorAll(Array.from(BLOCKED_TAGS).join(',')))) {
    blocked.remove();
  }

  for (const element of Array.from(doc.body.querySelectorAll('*'))) {
    const tag = element.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      const parent = element.parentNode;
      if (parent) {
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }
        parent.removeChild(element);
      }
      continue;
    }

    for (const attr of Array.from(element.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value;

      if (name.startsWith('on') || name === 'style' || name === 'srcset') {
        element.removeAttribute(attr.name);
        continue;
      }

      if (name === 'href' && !isSafeHref(value)) {
        element.removeAttribute(attr.name);
        continue;
      }

      if (name === 'src' && !isSafeSrc(value)) {
        element.removeAttribute(attr.name);
        continue;
      }

      if (name === 'target') {
        element.setAttribute('target', '_blank');
      }
    }

    if (tag === 'a' && element.getAttribute('href')) {
      element.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  }

  return doc.body.innerHTML;
};

export const extractPlainText = (html: string): string => {
  if (!html) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};
